// src/pages/ProvisionalOfferPage.tsx (This is your main "Summary" page for the stepper)
import React, { useEffect, useState } from "react";

type BreDataType = {
  BRE_ID?: string;
  Provisional_Offer?: string;
  Max_Loan_Amount?: string;
  Max_Tenure?: string;
  Max_Roi?: string;
  Repayment_Type?: string;
  Scheme_Id?: string;
  Academic_Deviation?: string;
};
import { Box, Container } from "@mui/material";
import ProvisionalOfferCard from "../../components/common/ProvisionalOfferCard";
import ConditionsForValidity from "../common-features/ConditionsForValidity";
import SummaryLoanDetails from "./SummaryLoanDetails";
import CompletionStatus from "../common-features/CompletionStatus";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import {
  setBreData,
  setIsProvisionalOffer,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import NoProvisionalOfferCard from "../../components/common/NoProvisionalOfferCard";
import OfferCheckLoader from "../../components/ui/OfferCheckLoader";
import { trackGooglePixelEvent } from "../../utils/ga";

// IMPORTANT: This component *IS* the "Summary" page for your stepper.
// It will contain all the sections of the Provisional Offer UI.
const ProvisionalOfferPage: React.FC = () => {
  const trackingId = useSelector(
    (state: RootState) => state?.stepper?.trackingId
  );
  const allFormValues = useSelector(
    (state: RootState) => state?.stepper?.formData
  );
  const notInList = useSelector(
    (state: RootState) => state?.stepper?.notInList
  );
  const breStatus = useSelector(
    (state: RootState) => state?.stepper?.breStatus
  );
  const breData: BreDataType = useSelector(
    (state: RootState) => state.stepper.breData
  );
  const provisionalOffer = useSelector(
    (state: RootState) => state.stepper.breData
  );

  const applicationSource = useSelector(
    (state: RootState) => state.stepper.applicationSource
  );
  const coAppAddOption = useSelector(
    (state: RootState) => state?.stepper?.coAppAddOption
  );
  const googleCampKeys = useSelector(
        (state: RootState) => state?.stepper?.googleCampKeys
  );

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
    useState(false);

  function extractCoApplicants(data: any) {
    const grouped: any = {};

    // Group keys by co-applicant index and prefix
    for (const key in data) {
      // Match keys like basicCoApplicant1Field, addressCoApplicant1Field, employmentCoApplicant1Field
      const match = key.match(
        /^(basic|address|employment)CoApplicant(\d+)(.+)$/
      );
      if (match) {
        const [, prefix, index, field] = match;
        if (!grouped[index]) grouped[index] = {};
        if (!grouped[index][prefix]) grouped[index][prefix] = {};
        grouped[index][prefix][field.charAt(0).toLowerCase() + field.slice(1)] =
          data[key];
      }
    }

    // Transform into required format, skipping any record missing all of firstName, lastName, gender, or dob
    return Object.keys(grouped)
      .sort((a: any, b: any) => a - b)
      .map((index: any) => {
        const basic = grouped[index]?.basic || {};
        const address = grouped[index]?.address || {};
        const employment = grouped[index]?.employment || {};

        // Skip if any of the required fields are missing or empty
        if (
          !basic.firstName ||
          !basic.lastName ||
          !basic.gender ||
          !basic.dob
        ) {
          return null;
        }

        return {
          oApplName: {
            sFirstName: basic.firstName || "",
            sLastName: basic.lastName || "",
            sMiddleName: basic.middleName || "",
          },
          sApplGndr: basic.gender || "male",
          Bureau_Trigger:  true,
          sDob: basic.dob ? basic.dob.split("-").reverse().join("") : "",
          aEmail: basic.email ? [{ sEmailAddr: basic.email }] : [],
          aKycDocs: basic.panNumber
            ? [{ sKycName: "PAN", sKycNumber: basic.panNumber }]
            : [],
          aPhone: basic.phoneNumber
            ? [
              {
                sPhoneType: "MOBILE",
                sAreaCode: "",
                sCountryCode: "+91",
                sExt: "",
                sPhoneNumber: basic.phoneNumber,
                sPhoneNumber1: "",
              },
            ]
            : [],
          sApplName: `${basic.firstName || ""} ${basic.lastName || ""}`.trim(),
          sRelationWithStudent: basic.relationWithStudent || "",
          bIsEmailVerified:
            basic.isEmailVerified === true || basic.isEmailVerified === "true",
          bIsMobileVerified:
            basic.isMobileVerified === true ||
            basic.isMobileVerified === "true",
          bIsPanVerified:
            basic.isPanVerified === true || basic.isPanVerified === "true",
          aAddr: [
            {
              sLine1: address.permanentAddressLine1 || "",
              sLine2: address.permanentAddressLine2 || "",
              sLine3: address.permanentAddressLine3 || "",
              sCity: address.permanentCity || "",
              iPinCode: String(address.permanentPincode) || "",
              sState: address.permanentState || "",
              sCountry: address.permanentCountry || "",
              sAddrType:
                allFormValues?.permanentOwnershipStatus == "Rented" || "Owned"
                  ? "PERMANENT ADDRESS"
                  : "",
            },
          ],
          // aAddr: [], // Placeholder
        };
      })
      .filter(Boolean); // Remove nulls
  }

  const handleShowCourseScores = (data: any) => {
    // Map scores dynamically based on course type fields
    const other_scores: { exam_type: string; exam_score: string }[] = [];
    const typeToKey: Record<string, string> = {
      "10th": "ssc_score",
      "12th": "hsc_score",
      // Diploma: "ug_score",
      // Graduation: "bachelor_score",
      Diploma: "diploma_score",
      Graduation: "ug_score",
    };
    const scores: Record<string, string> = {};

    // Loop through possible course slots (1-4)
    for (let i = 1; i <= 4; i++) {
      const typeKey = `course${i}Type`;
      const percKey = `course${i}Percentage`;
      const type = data[typeKey];
      const perc = data[percKey];
      if (type && perc && typeToKey[type]) {
        scores[typeToKey[type]] = perc;
      }
    }

    // Collect all core exam scores (with and without suffix)
    Object.keys(data)?.forEach((key) => {
      const coreMatch = key.match(/^coreExamName(_\d+)?$/);
      if (coreMatch) {
        const examName = data?.[key];
        const suffix = coreMatch[1] || "";
        const scoreKey = `coreExamScore${suffix}`;
        const examScore = data?.[scoreKey];
        if (examName && examScore) {
          other_scores.push({
            exam_type: examName,
            exam_score: examScore,
          });
        }
      }
      const langMatch = key.match(/^languageExamName(_\d+)?$/);
      if (langMatch) {
        const examName = data?.[key];
        const suffix = langMatch[1] || "";
        const scoreKey = `languageExamScore${suffix}`;
        const examScore = data?.[scoreKey];
        if (examName && examScore) {
          other_scores.push({
            exam_type: examName,
            exam_score: examScore,
          });
        }
      }
    });

    // Filter out empty scores and only include non-empty values
    const filteredScores: Record<string, string> = {};
    Object.entries(scores)?.forEach(([key, value]) => {
      if (value && value?.toString()?.trim() !== "") {
        filteredScores[key] = value as string;
      }
    });

    return {
      ...filteredScores,
      other_scores,
    };
  };

  const handleFetchBreApi = async () => {
    try {
      const payload = {
        applicationId: trackingId,
        name: "getProvisionalOffer",
      };
      const res: any = await eligibilityCheckServices.fetchBreApi(payload);
      if (res?.status === 200) {
        setIsLoading(false);
        dispatch(setBreData(res?.data?.data));
        if (res?.data?.status != "success") {
          setIsContinueButtonDisabled(true);
          dispatch(setIsProvisionalOffer(false));
          return;
        }

        dispatch(setBreData(res?.data?.data));
        dispatch(
          setIsProvisionalOffer(
            res?.data?.data?.Provisional_Offer?.toLowercase() === "yes"
          )
        );
      } else {
        setIsLoading(false);
        dispatch(
          setStatusPopup({
            open: true,
            message: res?.data?.message || "Something went wrong",
            status: "error",
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCourceDetail = () => {

    let courceDetailsList: any =
    {
      // institute_code: notInList?.includes("Institute Name")
      //   ? ""
      //   : typeof allFormValues?.institute === "string"
      //     ? allFormValues?.institute?.split("-")?.[0] || ""
      //     : allFormValues?.institute?.code || "",
      // course_code: notInList?.includes("Course Name")
      //   ? ""
      //   : typeof allFormValues?.courseName === "string"
      //     ? allFormValues?.courseName?.split("-")?.[0] || ""
      //     : allFormValues?.courseCode || "",
      // university_campus_id: notInList?.includes("University Name")
      //   ? ""
      //   : typeof allFormValues?.university === "string"
      //     ? allFormValues?.university?.split("-")?.[0] || ""
      //     : allFormValues?.university?.code || "",

      // university_country_id:
      //   allFormValues?.country?.value ||
      //   allFormValues?.universityCountryId ||
      //   "",
      category:
        allFormValues?.courseType?.value ||
        allFormValues?.courseType ||
        "",
      admission_status:
        allFormValues?.admissionStatus?.value ||
        allFormValues?.admissionStatus ||
        "",
      course_category:
        allFormValues?.courseCategory == "PG"
          ? "Post Graduation"
          : "Under Graduation",
      col_sec_type: "SECURED",
    }
    console.log(allFormValues,"allFormValues",notInList)
    if (applicationSource !== "" && applicationSource !==null) {

      if (allFormValues?.instituteNotInList == "false") {
        courceDetailsList.institute_code = allFormValues?.institute_code || "";
      }
      if (allFormValues?.courseNameNotInList == "false") {
        courceDetailsList.course_code = allFormValues?.courseCode || "";
      }
      if (allFormValues?.universityNotInList == "false") {
        courceDetailsList.university_campus_id = allFormValues?.universityCampusId || "";
      }
      courceDetailsList = {
        ...courceDetailsList,
        university_country_id: allFormValues?.universityCountryId || "",
      }
    } else {

      if (!notInList?.includes("Institute Name")) {
        courceDetailsList.institute_code = typeof allFormValues?.institute === "string"
          ? allFormValues?.institute?.split("-")?.[0] || ""
          : allFormValues?.institute?.code || "";
      }
      if (!notInList?.includes("Course Name")) {
        courceDetailsList.course_code = typeof allFormValues?.courseName === "string"
          ? allFormValues?.courseName?.split("-")?.[0] || ""
          : allFormValues?.courseCode || "";
      }
      if (!notInList?.includes("University Name")) {
        courceDetailsList.university_campus_id = typeof allFormValues?.university === "string"
          ? allFormValues?.university?.split("-")?.[0] || ""
          : allFormValues?.university?.code || "";
      }
      
      courceDetailsList = {
        ...courceDetailsList,
        university_country_id:
          allFormValues?.country?.value ||
          allFormValues?.universityCountryId ||
          "",
      }
    }

    return [courceDetailsList];
  }

  const isBureauTrigger = (coApplicantsCount: number, allFormValues: any): boolean => {
    let bureauTrigger: boolean = false;
    if(coApplicantsCount === 0) {
      bureauTrigger = true;
    } else if(coApplicantsCount > 0 && allFormValues?.pan && allFormValues?.pan?.length > 3 && (allFormValues?.isPanVerified === "true" || allFormValues?.isPanVerified === true)) {
      bureauTrigger = true;
    } else if(coApplicantsCount > 0 && !allFormValues?.pan && !(allFormValues?.isPanVerified === "true" || allFormValues?.isPanVerified === true)) {
      bureauTrigger = false;
    }
    return bureauTrigger;
  }

  useEffect(() => {
    const getProvisionalOffer = async () => {
      const coApplicants = extractCoApplicants(allFormValues);
      const course_scores = handleShowCourseScores(allFormValues);

      const payload = {
        applicationId: trackingId,
        provisionalOfferBody: {
          values: {
            input: {
              Unique_Lead_ID: trackingId,
              Requested_Loan_Amount: allFormValues?.loanAmount || "",
              oApplicant: {
                Bureau_Trigger: isBureauTrigger(coApplicants?.length, allFormValues),
                sApplGndr: allFormValues?.gender || "",
                sDob:
                  typeof allFormValues?.dob === "string"
                    ? allFormValues?.dob?.split("-")?.reverse()?.join("") || ""
                    : "",
                oApplName: {
                  sFirstName: allFormValues?.firstName || "",
                  sLastName: allFormValues?.lastName || "",
                  // "sMiddleName": allFormValues.middleName || ""
                },
                aAddr: [
                  {
                    sLine1: allFormValues?.permanentAddressLine1 || "",
                    sLine2: allFormValues?.permanentAddressLine2 || "",
                    sLine3: allFormValues?.permanentAddressLine3 || "",
                    sCity: allFormValues?.permanentCity || "",
                    iPinCode: String(allFormValues?.permanentPincode) || "",
                    sState: allFormValues?.permanentState || "",
                    sCountry: allFormValues?.permanentCountry || "",
                    sAddrType:
                      allFormValues?.permanentOwnershipStatus == "Rented" ||
                        "Owned"
                        ? "PERMANENT ADDRESS"
                        : "",
                    // "Metro_Flag": allFormValues.permanentMetroFlag || ""
                  },
                ],
                aEmail: [
                  {
                    sEmailAddr: allFormValues?.email || "",
                  },
                ],
                aKycDocs: [
                  {
                    sKycName: "PAN",
                    sKycNumber:
                      allFormValues?.panNumber ||
                      (allFormValues?.pan &&
                        allFormValues?.pan !== "" &&
                        allFormValues?.pan !== "yes" &&
                        allFormValues?.pan !== "no"
                        ? allFormValues?.pan
                        : ""),
                  },
                ],
                aPhone: [
                  {
                    sPhoneType: "MOBILE",
                    sAreaCode: String(allFormValues?.permanentPincode) || "",
                    sCountryCode: "+91",
                    sExt: "",
                    sPhoneNumber: allFormValues?.phone_number || "",
                    sPhoneNumber1: "",
                  },
                ],
                aCourseDetails: handleCourceDetail(),
                aAcademics: [course_scores],
              },
              aCoApplicant: coApplicants,
            },
          },
        },
      };

      try {
        const breResp: any =
          await eligibilityCheckServices.checkOfferProvisional(payload);
        if (breResp?.status === 200) {
          if(googleCampKeys && Object.keys(googleCampKeys).length && googleCampKeys.provisional_camp) {
            trackGooglePixelEvent(googleCampKeys.provisional_camp) 
          }
          setIsLoading(false);
          dispatch(setBreData(breResp?.data?.data));
          if (breResp?.data?.data?.Provisional_Offer == "No") {
            setIsContinueButtonDisabled(true);
          }
        } else {
          setIsLoading(false);
          dispatch(
            setStatusPopup({
              open: true,
              message: breResp?.data?.message || "Something went wrong",
              status: "error",
            })
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    const isBlankBreData =
      typeof breData === "undefined" ||
      (breData &&
        Object.values(breData).every(
          (v) => v === "" || v === null || typeof v === "undefined"
        ));

    switch (breStatus) {
      case "FROM_FORM":
        getProvisionalOffer();
        break;
      case "FROM_APPLICATION":
        if (isBlankBreData) {
          getProvisionalOffer();
        } else {
          handleFetchBreApi();
        }
        break;
      default:
        break;
    }
  }, [trackingId, allFormValues, breStatus]);

  return (
    <Box>
      {isLoading ? (
        <>
          {/* Loader Backdrop for BRE checkOffer API */}
          <OfferCheckLoader
            open={isLoading}
            content="We are checking your provisional offer"
          />
        </>
      ) : !provisionalOffer?.Provisional_Offer ||
        provisionalOffer?.Provisional_Offer === "No" ? (
        <Container
          maxWidth="lg"
          sx={{ py: 3, px: { xs: 2, sm: 3 } }}
          className="provisional-offer"
        >
          <NoProvisionalOfferCard />
        </Container>
      ) : (
        <Container
          maxWidth="lg"
          sx={{ pb: 3, px: { xs: 0, sm: 2 } }}
          className="provisional-offer"
        >
          {/* 2. Provisional Offer Card */}
          <Box sx={{ mt: 3 }}>
            <ProvisionalOfferCard
              studentName={allFormValues?.firstName || ""}
            />
          </Box>
          {/* 3. Loan Details */}
          <SummaryLoanDetails data={provisionalOffer} isLoading={isLoading} />
          {/* 4. Completion Status */}
          <CompletionStatus
            isContinueButtonDisabled={isContinueButtonDisabled}
          />
          {/* 5. Conditions for Validity */}
          <ConditionsForValidity />
        </Container>
      )}
    </Box>
  );
};

export default ProvisionalOfferPage;
